// Scenario families: a search resolves to the FAMILY, then the reader picks
// the variant that matches their situation. The cost-quartile levels mirror
// ScenarioFamilyModal / HomeBeta exactly (base = severity 0; the rest bucketed
// by their rank in the family's national cost spread), so Atlas agrees with
// the scenario pages about which variant sits where.
import type { AtlasScenario } from "./data";

export const LEVEL_LABELS = ["Base", "Low-cost", "Mid-cost", "High-cost", "Top-cost"] as const;
export const LEVEL_QUARTILE = ["no complications", "1st quartile", "2nd quartile", "3rd quartile", "4th quartile"] as const;
export const LEVEL_COLORS = ["#2b5e49", "#5a9a3c", "#C5A55A", "#e67e22", "#B22234"] as const;

export interface FamilyMember {
  s: AtlasScenario;
  level: 0 | 1 | 2 | 3 | 4;
  isBase: boolean;
  /** national total relative to the base (1 = same) */
  xBase: number | null;
}

export interface Family {
  id: string;                 // scenario_family_id, or the scenario id for singletons
  name: string;               // the family's shared stem
  cat: string;
  members: FamilyMember[];    // sorted by level, then cost
  byLevel: FamilyMember[][];  // 5 buckets
  defaultId: string;          // the ideal-path variant to start on
  baseCost: number | null;
}

const ESCALATION = /\b(ER|emergency|escalat|ambulance|ICU|intensive care|complication)/i;

/** Strip the variant suffix ("Name — with X") to the family stem. */
export function familyStem(name: string, variant?: string): string {
  let n = name;
  const dash = n.indexOf(" — ");
  if (dash > 0) n = n.slice(0, dash);
  if (variant && n.toLowerCase().endsWith(variant.toLowerCase())) n = n.slice(0, n.length - variant.length).replace(/[\s—-]+$/, "");
  return n.trim();
}

export function buildFamilies(scenarios: AtlasScenario[]): Map<string, Family> {
  const groups = new Map<string, AtlasScenario[]>();
  for (const s of scenarios) {
    const key = s.fam ?? s.id;
    const arr = groups.get(key) ?? [];
    arr.push(s);
    groups.set(key, arr);
  }
  const out = new Map<string, Family>();
  for (const [id, list] of groups) {
    // Base = severity 0 members; if none or all, the cheapest is the base.
    let baseIds = new Set(list.filter((s) => (s.sev ?? 0) === 0).map((s) => s.id));
    if (baseIds.size === 0 || baseIds.size === list.length) {
      const cheapest = [...list].sort((a, b) => (a.nc ?? 0) - (b.nc ?? 0))[0];
      baseIds = new Set([cheapest.id]);
    }
    const nonBase = list.filter((s) => !baseIds.has(s.id)).sort((a, b) => (a.nc ?? 0) - (b.nc ?? 0));
    const n = nonBase.length;
    const levelOf = new Map<string, 0 | 1 | 2 | 3 | 4>();
    list.filter((s) => baseIds.has(s.id)).forEach((s) => levelOf.set(s.id, 0));
    nonBase.forEach((s, idx) => {
      const pct = n <= 1 ? 1 : idx / (n - 1);
      levelOf.set(s.id, pct <= 0.25 ? 1 : pct <= 0.5 ? 2 : pct <= 0.75 ? 3 : 4);
    });
    const bases = list.filter((s) => baseIds.has(s.id)).sort((a, b) => (a.nc ?? 0) - (b.nc ?? 0));
    const baseCost = bases[0]?.nc ?? null;
    const members: FamilyMember[] = list.map((s) => ({
      s, level: levelOf.get(s.id) ?? 0, isBase: baseIds.has(s.id),
      xBase: baseCost && s.nc ? s.nc / baseCost : null,
    })).sort((a, b) => a.level - b.level || (a.s.nc ?? 0) - (b.s.nc ?? 0));
    const byLevel: FamilyMember[][] = [[], [], [], [], []];
    members.forEach((m) => byLevel[m.level].push(m));
    // Ideal path: the base — unless the base itself reads as an escalation
    // (an ER visit is never the uncomplicated path); then the cheapest member
    // that doesn't, if there is one.
    let def = bases[0] ?? members[0].s;
    if (ESCALATION.test(def.n) && list.length > 1) {
      const calm = [...list].filter((s) => !ESCALATION.test(s.n)).sort((a, b) => (a.nc ?? 0) - (b.nc ?? 0))[0];
      if (calm) def = calm;
    }
    const stemSource = bases[0] ?? members[0].s;
    out.set(id, { id, name: familyStem(stemSource.n, stemSource.var), cat: stemSource.cat, members, byLevel, defaultId: def.id, baseCost });
  }
  return out;
}

/** "with postpartum hemorrhage · High-cost (3rd quartile)" */
export function variantCaption(m: FamilyMember): string {
  const bits: string[] = [];
  if (m.s.var && m.s.var !== "uncomplicated") bits.push(m.s.var);
  else if (m.isBase) bits.push("uncomplicated path");
  bits.push(`${LEVEL_LABELS[m.level]} (${LEVEL_QUARTILE[m.level]})`);
  return bits.join(" · ");
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
export function money0(n: number): string { return usd.format(n); }
